/**
 * The bridge between a screen doing something and the assistant saying what
 * followed.
 *
 * Screens should not have to know what she needs. A page that has just moved a
 * work order knows the order and the new status and nothing else; gathering the
 * site's remaining counts is her problem, so it lives here rather than in
 * `WorkOrderDetailPage`.
 *
 * Two rules this file keeps:
 *
 *  - It never reports something that did not happen. Every caller invokes it
 *    from the mutation's `onSuccess`, after the platform has confirmed, so she
 *    is describing the record rather than the request.
 *
 *  - It never claims a consequence the platform does not model. Work orders
 *    here carry no dependency field, so nothing is passed as `unblocked` and
 *    she says nothing about released work — rather than inferring a dependency
 *    from a shared zone, which would be a guess dressed as a fact.
 */
import { useCallback } from "react";
import { useSession } from "../app/SessionContext";
import { useAssistant } from "./AssistantContext";
import { isOpen, type WorkOrder, type WorkOrderStatus } from "../domain/workOrders";

export interface Reporter {
  workOrderMoved: (wo: WorkOrder, from: WorkOrderStatus) => void;
  workOrderRaised: (wo: WorkOrder) => void;
}

export function useReporter(): Reporter {
  const { source, site } = useSession();
  const { noteAction } = useAssistant();

  /* Counted after the move, from the platform rather than from local state, so
     the number she quotes is the number the next screen will show. */
  const openCount = useCallback(async () => {
    const page = await source.listWorkOrders({ siteId: site.id }).catch(() => null);
    if (!page) return undefined;
    return page.items.filter((w) => isOpen(w.status)).length;
  }, [site.id, source]);

  const workOrderMoved = useCallback(
    (wo: WorkOrder, from: WorkOrderStatus) => {
      void openCount().then((openWorkOrders) =>
        noteAction(
          { kind: "work-order-transition", id: wo.number, from, to: wo.status, title: wo.title },
          { openWorkOrders },
        ),
      );
    },
    [noteAction, openCount],
  );

  const workOrderRaised = useCallback(
    (wo: WorkOrder) => {
      void openCount().then((openWorkOrders) =>
        noteAction(
          { kind: "work-order-created", id: wo.number, priority: `P${wo.priority}`, title: wo.title },
          { openWorkOrders },
        ),
      );
    },
    [noteAction, openCount],
  );

  return { workOrderMoved, workOrderRaised };
}
